import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import { dashboardAPI } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import standaloneCourseApi from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import { toQueryString as toQS } from '@Utilities/toQueryString'

export type CheckoutBundlePayload = {
	bundle_id: number
	payment_method: 'wallet' | 'scratchCard' | 'free'
	promocode?: string
	/** scratch-card code — its value must EQUAL the bundle price */
	code?: string
}

/**
 * Bundles — the /bundles namespace. Featured (admin-curated), details (public,
 * by id or label), mine (purchases), checkout and promocode verify. Listing/
 * search goes through /search/bundles (SearchApi). Checkout enrolls the student
 * in every member course, so it cross-invalidates the standalone-course cache.
 */
export const bundleApi = BaseApi.enhanceEndpoints({
	addTagTypes: ['BUNDLE', 'BUNDLE_MINE', 'BUNDLE_FEATURED'],
}).injectEndpoints({
	endpoints: builder => ({
		getFeaturedBundles: builder.query<ApiResponse<Bundle[]>, void>({
			query: () => '/bundles/featured',
			providesTags: ['BUNDLE_FEATURED'],
		}),
		getBundleDetails: builder.query<ApiResponse<Bundle>, string>({
			query: identifier => `/bundles/details/${identifier}`,
			providesTags: result => (result ? [{ type: 'BUNDLE' as const, id: result.data.id }] : ['BUNDLE']),
		}),
		// Returns ENROLLMENT rows (bundle nested under `bundle`), paginated.
		getMyBundles: builder.query<ApiResponse<BundleEnrollment[]>, { page?: number; perPage?: number } | void>({
			query: args => `/bundles/mine?${toQS({ page: 1, perPage: 50, ...(args ?? {}) })}`,
			providesTags: ['BUNDLE_MINE'],
		}),
		checkoutBundle: builder.mutation<ApiResponse<BundleCheckoutResult>, CheckoutBundlePayload>({
			query: payload => ({
				url: '/checkout/bundle',
				method: 'POST',
				body: payload,
			}),
			async onQueryStarted(arg, api) {
				try {
					const { data: response } = await api.queryFulfilled
					// Wallet balance + bundle ownership state.
					api.dispatch(dashboardAPI.util.invalidateTags(['Profile']))
					api.dispatch(
						bundleApi.util.invalidateTags([
							{ type: 'BUNDLE', id: arg.bundle_id },
							'BUNDLE_MINE',
							'BUNDLE_FEATURED',
						])
					)
					// The purchase also enrolled the member courses — flip their
					// cached detail/access/mine state without a reload.
					const enrolledIds = response?.data?.enrolled_course_ids ?? []
					api.dispatch(
						standaloneCourseApi.util.invalidateTags([
							'STANDALONE_MINE',
							'STANDALONE_FEATURED',
							...enrolledIds.map(id => ({ type: 'STANDALONE_COURSE' as const, id })),
							...enrolledIds.map(id => ({ type: 'STANDALONE_COURSE' as const, id: `access-${id}` })),
						])
					)
				} catch {}
			},
		}),
		// Bundle promocodes have their own endpoint (course/classroom-targeted
		// codes are rejected here and vice versa).
		verifyBundlePromocode: builder.mutation<ApiResponse<{ value: number }>, { bundle_id: number; code: string }>({
			query: body => ({
				url: '/bundles/promocodes/verify',
				method: 'POST',
				body,
			}),
		}),
		// invoices.* + joined bundle_title/transaction_ref, paginated — the
		// باقات source of the activity hub's invoices tab.
		getBundleInvoices: builder.query<ApiResponse<BundleInvoice[]>, { page?: number; perPage?: number }>({
			query: args => `/bundles/invoices?${toQS(args)}`,
		}),
	}),
})

export const {
	useGetFeaturedBundlesQuery,
	useGetBundleDetailsQuery,
	useGetMyBundlesQuery,
	useCheckoutBundleMutation,
	useVerifyBundlePromocodeMutation,
	useGetBundleInvoicesQuery,
} = bundleApi

export default bundleApi
